// lib/axios.ts
import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios';
import { NO_AUTH_REQUIRED_API_ROUTES, API_ROUTES, STORAGE_KEYS } from '@/constants';
import { useAuthStore } from '@/stores/useAuthStore';

// ============================================
// Instance
// ============================================

export const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/',
});

// ============================================
// Helpers
// ============================================

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

function clearAuthAndRedirect() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    useAuthStore.setState({ isLogged: false, isLoading: false });
}

function tryRefresh(): Promise<string | null> {
    if (isRefreshing && refreshPromise) return refreshPromise;

    isRefreshing = true;
    const refresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refresh) {
        isRefreshing = false;
        return Promise.resolve(null);
    }

    refreshPromise = axios.post<{ access: string; refresh: string }>(
        `${import.meta.env.VITE_API_URL || ''}${API_ROUTES.refresh}`,
        { refresh },
    )
    .then(({ data }) => {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh);
        return data.access;
    })
    .catch(() => {
        return null;
    })
    .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
    });

    return refreshPromise;
}

// ============================================
// Request Interceptor
// ============================================

api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (NO_AUTH_REQUIRED_API_ROUTES.includes(config.url || '')) {
            return config;
        }

        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ============================================
// Response Interceptor
// ============================================

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const { response, config: originalRequest } = error;

        if (!response || !originalRequest) {
            return Promise.reject(error);
        }

        if (response.status !== 401) {
            return Promise.reject(error);
        }

        const url = originalRequest.url || '';

        // Don't retry auth endpoints
        if (NO_AUTH_REQUIRED_API_ROUTES.includes(url) || url === API_ROUTES.refresh) {
            return Promise.reject(error);
        }

        // Already retried this request
        if ((originalRequest as any)._retried) {
            clearAuthAndRedirect();
            return Promise.reject(error);
        }

        (originalRequest as any)._retried = true;

        const newToken = await tryRefresh();
        if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        }

        clearAuthAndRedirect();
        return Promise.reject(error);
    }
);
